'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { SimilarProductsDocument } from '@packages/gql/generated/shopping/SimilarProductsDocument';
import { useMemo } from 'react';
import { useQuery } from 'urql';
import { useIntl } from 'react-intl';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel/RecoCarousel.client';
import { RecoProductCard } from '../../RecoProductCard/RecoProductCard.client';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type SimilarProductsRecoSliderProps = Pick<RequestParams, 'productId'> &
  Omit<ProductsRecoSliderProps, 'preferTitle'>;

/* GraphQL */ `
  query SimilarProducts($productId: String!) {
    similarProducts(productId: $productId) {
      type
      recommendations {
        ...RecoProductCard
      }
    }
  }
`;

const RDE_TYPE: RdeType = 'similarProducts';

/**
 * SimilarProductsRecoSlider component description displayed in storybook
 * */
export const SimilarProductsRecoSlider = ({
  backgroundColor,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  productId,
  title,
}: SimilarProductsRecoSliderProps) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: SimilarProductsDocument,
    variables: {
      productId,
    },
    context: useMemo(
      () => ({ suspense: false, fetchOptions: { headers: { 'x-locale': locale } } }),
      [locale],
    ),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.similarProducts.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) => getFragmentData(RecoProductCardFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.similarProducts.type;

  useProductsRecoTracking({
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title,
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <RecoCarousel
      title={title}
      loadingState={!filteredRecommendations}
      backgroundColor={backgroundColor}
      ref={recoEl}
    >
      {filteredRecommendations?.map((recommendation, idx) => (
        <RecoProductCard
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          data={recommendation}
          onClick={(akl, sku) => {
            onProductClick?.(akl, sku);
          }}
          position={idx + 1}
          rdeTypeAbbr={rdeTypeAbbr}
        />
      ))}
    </RecoCarousel>
  );
};
